import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface UserType {
  id: number;
  firstName: string;
  lastName: string;
  mobile: string;
  avatar: string;
  token: string;
}

interface UserStoreState {
  dataUser: UserType | null;
  updateUser: (partialUser: Partial<UserType>) => void;
  addUser: (user: UserType) => void;
  deleteUser: () => void;
}

// 12 hours in ms
const TTL = 1000 * 60 * 60 * 12;

// Build a “raw” storage driver that reads/writes { value, expires } wrappers
const rawTTLStorage = {
  getItem: (name: string): string | null => {
    const raw = localStorage.getItem(name);
    if (!raw) return null;
    try {
      const { value, expires } = JSON.parse(raw);
      // expired?
      if (Date.now() > expires) {
        localStorage.removeItem(name);
        return null;
      }
      // return the *inner* JSON so createJSONStorage will parse it as your state
      return JSON.stringify(value);
    } catch {
      localStorage.removeItem(name);
      return null;
    }
  },
  setItem: (name: string, serializedValue: string) => {
    // serializedValue is the JSON.stringify of your state slice
    const expires = Date.now() + TTL;
    localStorage.setItem(
      name,
      JSON.stringify({ value: JSON.parse(serializedValue), expires })
    );
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

const useUserStore = create<UserStoreState>()(
  persist(
    (set, get) => ({
      dataUser: null,

      updateUser: (partialUser) => {
        set((state) => ({
          dataUser: state.dataUser
            ? { ...state.dataUser, ...partialUser }
            : null,
        }));
      },

      addUser: (user) => {
        set({ dataUser: user });
      },

      deleteUser: () => {
        set({ dataUser: null });
      },
    }),
    {
      name: "user-store",
      // wrap your TTL layer in createJSONStorage so types line up
      storage: createJSONStorage(() => rawTTLStorage),
    }
  )
);

export default useUserStore;
